import '@oddbird/popover-polyfill';
import React, {
  ReactNode,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';

/**
 * A side panel component that uses the Popover API to slide in from the right side of the screen.
 * It encapsulates its own open/close logic, including a slide-out animation.
 *
 * ---
 *
 * ### How to use:
 *
 * 1. **Create a ref** in your parent component to get access to the panel's methods:
 *    ```
 *    const panelRef = useRef<SidePanelHandle | null>(null);
 *    ```
 *
 * 2. **Attach the ref** to the `SidePanel` component:
 *    ```
 *    <SidePanel ref={panelRef} onClose={() => setIsPanelOpen(false)}>
 *      ... content ...
 *    </SidePanel>
 *    ```
 *
 * 3. **Control the panel** using the exposed methods on the ref:
 *    - To open: `panelRef.current?.showPopover();`
 *    - To close: `panelRef.current?.close();` (This will trigger the exit animation)
 *
 * 4. **Manage state** with the `onClose` callback. The `onClose` prop is recommended
 *    to synchronize the parent component's state with the panel's visibility.
 *
 * ---
 * @exports SidePanel - The main component.
 * @exports SidePanelHandle - The type definition for the imperative handle ref.
 */
const TOP_OFFSET = '56px';

const PopoverContainer = styled.div<{
  panelId: string;
  isVisible: boolean;
  width: string;
}>`
  #${(props) => props.panelId} {
    position: fixed;
    top: ${TOP_OFFSET};
    right: 0;
    left: auto;
    height: calc(100% - ${TOP_OFFSET});
    width: ${(props) => props.width};
    max-width: 100vw;
    box-sizing: border-box;
    background: ${FullPalette.WHITE};
    box-shadow: 0px 13px 15px 0px rgba(21, 55, 44, 0.13);
    border-top: 1px solid ${FullPalette.GREY_700};
    border-bottom: 1px solid ${FullPalette.GREY_700};
    border-left: 1px solid ${FullPalette.GREY_700};
    border-right: none;
    overflow-x: hidden;
    transition: transform 0.3s ease-in-out;
    transform: ${(props) =>
      props.isVisible ? 'translateX(0)' : 'translateX(100%)'};
    visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  }

  #${(props) => props.panelId}.closing {
    transform: translateX(100%);
  }
`;

export interface SidePanelHandle {
  showPopover: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: () => boolean;
}

interface SidePanelProps {
  children: ReactNode;
  id?: string;
  onClose?: () => void;
  isVisible?: boolean;
  disableClickOutside?: boolean;
  width?: number | string;
  onOutsideClick?: () => void;
}

const formatWidth = (width?: number | string) =>
  typeof width === 'number' ? `${width}px` : width || '500px';

const SidePanel = React.forwardRef<SidePanelHandle, SidePanelProps>(
  (
    {
      children,
      id = 'side_panel',
      onClose,
      isVisible = false,
      disableClickOutside = false,
      width = '500px',
      onOutsideClick,
    },
    ref
  ) => {
    const [isClosing, setIsClosing] = React.useState(false);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const computedWidth = formatWidth(width);

    // Handle click outside to close
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          isVisible &&
          !isClosing &&
          panelRef.current &&
          !panelRef.current.contains(event.target as Node)
        ) {
          const target = event.target as Element;

          // Don't close if there are any open modals or the aiFeedback Popover
          if (
            target.closest('.MuiPopover-root') ||
            target.closest('.MuiPopover-paper') ||
            target.closest('.MuiModal-root') ||
            target.closest('.MuiDialog-root') ||
            target.closest('[role="dialog"]') ||
            target.closest('[data-reach-menu-list]') ||
            document.querySelector(
              '.MuiPopover-root:not([style*="visibility: hidden"])'
            ) ||
            document.querySelector('.MuiModal-root') ||
            document.querySelector('.MuiDialog-root')
          ) {
            return;
          }

          // Don't close if clicking on table rows
          if (
            target.closest('[role="row"]') ||
            target.closest('.MuiDataGrid-row') ||
            target.closest('[data-testid="row"]') ||
            target.closest('tr')
          ) {
            return;
          }

          // Close the panel when clicking outside
          setIsClosing(true);
          setTimeout(() => {
            setIsClosing(false);
            if (onClose) {
              onClose();
            }
            if (onOutsideClick) {
              onOutsideClick();
            }
          }, 300);
        }
      };

      if (isVisible && !disableClickOutside) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isVisible, isClosing, onClose, onOutsideClick, disableClickOutside]);

    useImperativeHandle(ref, () => ({
      showPopover: () => {
        // Parent controls visibility, just cancel any closing animation
        if (isClosing) {
          setIsClosing(false);
        }
      },
      close: () => {
        if (!isVisible || isClosing) {
          return;
        }
        setIsClosing(true);
        // Delay callback to allow exit animation
        setTimeout(() => {
          setIsClosing(false);
          if (onClose) {
            onClose();
          }
        }, 300);
      },
      toggle: () => {
        if (isVisible && !isClosing) {
          // Close
          setIsClosing(true);
          setTimeout(() => {
            setIsClosing(false);
            if (onClose) {
              onClose();
            }
          }, 300);
        } else if (onClose) {
          // Let parent handle opening
          onClose();
        }
      },
      isOpen: () => isVisible && !isClosing,
    }));

    return (
      <PopoverContainer
        panelId={id}
        isVisible={isVisible}
        width={computedWidth}
      >
        <div id={id} ref={panelRef} className={isClosing ? 'closing' : ''}>
          {children}
        </div>
      </PopoverContainer>
    );
  }
);

SidePanel.displayName = 'SidePanel';

export default SidePanel;
